import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store'
import i18n, { loadSingleLocale } from '@/i18n'

Vue.use(Router)

/**
 * Note: sub-menu only appear when route children.length >= 1
 * Detail see: https://panjiachen.github.io/vue-element-admin-site/guide/essentials/router-and-nav.html
 *
 * hidden: true                   if set true, item will not show in the sidebar(default is false)
 * alwaysShow: true               if set true, will always show the root menu
 *                                if not set alwaysShow, when item has more than one children route,
 *                                it will becomes nested mode, otherwise not show the root menu
 * redirect: noRedirect           if set noRedirect will no redirect in the breadcrumb
 * name:'router-name'             the name is used by <keep-alive> (must set!!!)
 * meta : {
    roles: ['admin','editor']    control the page roles (you can set multiple roles)
    title: 'title'               the name show in sidebar and breadcrumb (recommend set)
    icon: 'svg-name'/'el-icon-x' the icon show in the sidebar
    breadcrumb: false            if set false, the item will hidden in breadcrumb(default is true)
    activeMenu: '/example/list'  if set path, the sidebar will highlight the path you set
  }
 */

/**
 * constantRoutes
 * a base page that does not have permission requirements
 * all roles can be accessed
 */
export const constantRoutes = [
  /* 首页部分路由，不需要登陆也可以查看*/
  {
    path: '/',
    redirect: '/index'
  },
  {
    path: '/index',
    name: 'Index',
    component: () => import('@/views/index/index/index')
  },
  {
    path: '/index/about',
    component: () => import('@/views/index/about')
  },
  {
    path: '/index/news',
    component: () => import('@/views/index/news')
  },
  {
    path: '/index/newInfo',
    component: () => import('@/views/index/news/newInfo')
  },
  {
    path: '/index/gamePage/bxzl',
    component: () => import('@/views/gamePage/bxzl.vue')
  },
  {
    path: '/index/404',
    component: () => import('@/views/404.vue'),
    hidden: true
  },
  {
    path: '/index/*', // 匹配所有以 /index/ 开头的未定义路径（如 /index/abc、/index/xxx）
    redirect: '/index/404', // 重定向到 /index/404 页面
    hidden: true
  },
  {
    path: '*',
    redirect: '/index/404', // 非 /index 开头的未知路径也跳转到 404
    hidden: true
  }
]

// Vue 2 中创建路由实例的方式
const router = new Router({
  mode: 'history',
  scrollBehavior: () => ({ y: 0 }),
  routes: constantRoutes
})

const hashList = ['#home', '#games', '#news', '#contact']

// 核心：拦截/index/并修正为/index
router.beforeEach(async(to, from, next) => {
  // 等待语言类型列表加载完成（避免初始时列表为空）
  if (!store.state.isLangListLoaded) {
    await store.dispatch('fetchLangList') // 确保列表已加载
  }
  const { langList } = store.state

  // 处理路径的核心逻辑
  const processPath = (path) => {
    // 空路径直接返回
    if (path === '/') return path

    // 连续斜杠处理为单斜杠
    let processed = path.replace(/\/+/g, '/')

    // 移除末尾斜杠（除了根路径）
    if (processed.length > 1 && processed.endsWith('/')) {
      processed = processed.slice(0, -1)
    }

    return processed
  }

  const normalizedPath = processPath(to.path)

  const lang = to.query.lang
  const newQuery = JSON.parse(JSON.stringify(to.query))
  if (!langList.includes(lang)) {
    i18n.locale = i18n.fallbackLocale
  } else {
    i18n.locale = lang || 'en'
  }

  let hash = JSON.parse(JSON.stringify(to.hash))
  if (hash && !hashList.includes(hash)) {
    hash = '#home'
  }

  // 如果是首次加载或语言变化，才加载语言包
  if (!i18n.messages[i18n.locale] || (lang !== i18n.locale && lang !== undefined && lang !== '')) {
    await loadSingleLocale(i18n.locale)
  }

  // 如果处理后的路径与原路径不同，则重定向
  if (normalizedPath !== to.path) {
    return next({
      path: normalizedPath,
      hash: hash,
      query: newQuery,
      replace: true // 替换历史记录，避免回退问题
    })
  }

  // 处理/index/的特殊情况
  if (to.path === '/index/') {
    return next({
      path: '/index',
      hash: hash,
      query: newQuery,
      replace: true
    })
  }

  if (lang !== newQuery.lang || hash !== to.hash) {
    return next({
      path: to.path,
      hash: hash,
      query: newQuery,
      replace: true
    })
  }

  next()
})

export function resetRouter() {
  const newRouter = new Router({
    mode: 'history',
    scrollBehavior: () => ({ y: 0 }),
    routes: constantRoutes
  })
  router.matcher = newRouter.matcher
}

export default router
